import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { useUserPreferences } from "./UserPreferencesContext";

const STORAGE_KEY = "novari_kids_dark_mode";

interface KidsDarkModeContextType {
  isDark: boolean;
  toggle: () => void;
}

const KidsDarkModeContext = createContext<KidsDarkModeContextType>({
  isDark: false,
  toggle: () => {},
});

export function KidsDarkModeProvider({ children }: { children: ReactNode }) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
  });
  // The single source of truth for the shared `dark`/`light` class on <html>
  // is UserPreferencesContext. This provider forces it to `dark` while a kids
  // UI is on screen (see comment below), but must restore the AMBIENT
  // preference — not assume "off" means "light" — when it turns off or
  // unmounts, otherwise an adult who prefers dark mode loses it the moment
  // they leave a kids flashcard session, with no re-render to fix it since
  // UserPreferencesContext's own effect only re-runs when the preference
  // itself changes, not when unrelated components mount/unmount.
  const { preferences } = useUserPreferences();
  const ambientMode = preferences?.theme_mode || 'dark';
  // The unmount cleanup below runs with an empty dep array (must fire only
  // once, on actual unmount) — a plain closure over `ambientMode` would
  // capture whatever it was at MOUNT time, not the latest value if the user
  // changes their theme preference while a kids session is still open. A ref
  // always reads the current value regardless of when the closure was created.
  const ambientModeRef = useRef(ambientMode);
  ambientModeRef.current = ambientMode;

  // Sync to body attribute so CSS / any component can react
  useEffect(() => {
    const DARK_BG = "#131f2e";
    // Carry the Kids tokens to dialogs and menus portalled outside AppShell.
    document.documentElement.setAttribute('data-kids-mode', 'true');
    if (isDark) {
      document.documentElement.setAttribute("data-kids-dark", "true");
      // Also add Tailwind's .dark class so CSS variables (bg-muted, bg-surface, etc.)
      // resolve to dark values in any shared/adult component visited from kids mode
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
      document.documentElement.style.colorScheme = "dark";
      // Paint the entire viewport dark — eliminates white/cream gutters
      document.documentElement.style.background = DARK_BG;
      document.body.style.background = DARK_BG;
    } else {
      document.documentElement.removeAttribute("data-kids-dark");
      document.documentElement.classList.remove("dark", "light");
      document.documentElement.classList.add(ambientMode);
      document.documentElement.style.colorScheme = ambientMode;
      document.documentElement.style.background = "";
      document.body.style.background = "";
    }
    try { localStorage.setItem(STORAGE_KEY, String(isDark)); } catch { /* ignore */ }

    // Forces the browser to actually repaint every layer under this toggle.
    // Without it, flipping dark mode can leave a scrolling container (the
    // dashboard's `overflow-y-auto` main panel is the one this was found on)
    // showing a stale, pre-toggle raster of part of its content — its own
    // background updates, colored borders/icons update, but a text block in
    // between stays painted with the old theme's background until something
    // else forces a reflow. The underlying DOM/CSS is already correct at
    // that point (verified via getComputedStyle), it's purely a compositor
    // paint that never got invalidated. Reading `offsetHeight` forces
    // layout, which reliably kicks the repaint. Found 2026-09-08 reviewing
    // Kids Mode dark-mode toggle feedback.
    requestAnimationFrame(() => {
      void document.body.offsetHeight;
    });
  }, [isDark, ambientMode]);

  // Clean up when the kids provider unmounts (e.g. user navigates away from
  // Kids Flashcards) — restore the ambient theme rather than force-clearing
  // `dark`, which would silently override an adult's real preference.
  useEffect(() => {
    return () => {
      document.documentElement.removeAttribute('data-kids-mode');
      document.documentElement.removeAttribute("data-kids-dark");
      document.documentElement.classList.remove("dark", "light");
      document.documentElement.classList.add(ambientModeRef.current);
      document.documentElement.style.colorScheme = ambientModeRef.current;
      document.documentElement.style.background = "";
      document.body.style.background = "";
    };
  }, []);

  const toggle = () => setIsDark(prev => !prev);

  return (
    <KidsDarkModeContext.Provider value={{ isDark, toggle }}>
      {children}
    </KidsDarkModeContext.Provider>
  );
}

export function useKidsDarkMode() {
  return useContext(KidsDarkModeContext);
}

// ── Colour tokens (Duolingo-parity dark mode) ──────────────────────────────
// Layer hierarchy (dark):  page < sidebar < card < elevated
export const KT = {
  // Surfaces — three distinct levels so cards visually "float" above the page
  page:         (d: boolean) => d ? "#131f2e" : "#f3f4f6",   // darkest base
  sidebar:      (d: boolean) => d ? "#1a2840" : "#ffffff",   // slightly elevated
  card:         (d: boolean) => d ? "#233044" : "#ffffff",   // card surface — clearly lighter than page
  cardHover:    (d: boolean) => d ? "#2a3a52" : "#f9fafb",  // subtle hover lift
  sidebarBorder:(d: boolean) => d ? "rgba(255,255,255,0.07)" : "#e5e7eb",
  divider:      (d: boolean) => d ? "rgba(255,255,255,0.06)" : "#f3f4f6",

  // Text
  heading:      (d: boolean) => d ? "#f9fafb" : "#111827",  // near-white / near-black
  body:         (d: boolean) => d ? "#cbd5e1" : "#374151",
  sub:          (d: boolean) => d ? "#94a3b8" : "#6b7280",
  navText:      (d: boolean) => d ? "#94a3b8" : "#4b5563",

  // Nav active
  navActiveBg:  (d: boolean) => d ? "rgba(88,204,2,0.18)" : "#edfbd4",
  navActive:    "#46A302",

  // Page-level header (sticky top bars)
  headerBg:     (d: boolean) => d ? "#1a2840" : "#ffffff",
  headerBorder: (d: boolean) => d ? "rgba(255,255,255,0.07)" : "#e5e7eb",
};
